import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { apiFetch, getStoredToken } from '../api';
import { useAuth } from './useAuth'; // Use the custom hook/context export

export const AlertContext = createContext(null);

export const useAlerts = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error("useAlerts must be used within an AlertProvider");
    }
    return context;
};

export const AlertProvider = ({ children }) => {
    // We need the token directly. Assuming useAuth provides user with token, 
    // or we fetch it from storage. 
    // Ideally AuthContext should provide the raw token if needed, or apiFetch handles it automatically.
    // Let's use apiFetch's auto-token logic, but we need to know WHEN to trigger (i.e. is logged in).
    const { user, token } = useAuth(); // Assuming useAuth exposes token or we get it from local storage inside apiFetch

    const [alerts, setAlerts] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showTenantAlerts, setShowTenantAlerts] = useState(false);

    // --- Enterprise Password Expiry UI Logic ---
    const checkPasswordExpiry = useCallback(() => {
        if (!user || !user.pwd_exp) return null;

        const userId = user.id || user.email;
        const nowMS = dayjs().valueOf();
        const expMS = user.pwd_exp * 1000;
        const diffMS = expMS - nowMS;

        const remainingDays = Math.floor(diffMS / 86400000);
        const todayDateStr = dayjs().format('YYYY-MM-DD');

        const alertId = `pwd-exp-${userId}-${todayDateStr}`;
        const readKey = `password_alert_read_${userId}_${todayDateStr}`;

        const isRead = localStorage.getItem(readKey) === 'true';
        const notificationCreated = (remainingDays >= 0 && remainingDays <= 7);

        // --- MANDATORY DEBUG LOGS [dbg-notif] ---
        // console.group('%c[Password Expiry Notification Debug]', 'color: #3b82f6; font-weight: bold;');
        // console.log('User:', user.email);
        // console.log('Remaining Days:', remainingDays);
        // console.log('Notification Created:', notificationCreated ? 'YES' : 'NO');
        // console.log('Already Read Today:', isRead ? 'YES' : 'NO');
        // console.groupEnd();

        if (notificationCreated) {
            return {
                id: alertId,
                title: "Password Expiring Soon",
                message: remainingDays === 0
                    ? "Your password will expire today. Please update your password immediately."
                    : `Your password will expire in ${remainingDays} day(s).`,
                alert_type: 'warning',
                is_read: isRead,
                created_at: dayjs(nowMS).toISOString(),
                is_virtual: true,
                cta: {
                    label: 'Change Password',
                    link: '/profile'
                }
            };
        }
        return null;
    }, [user]);

    const injectVirtualAlert = useCallback((backendAlerts, backendUnreadCount) => {
        const vAlert = checkPasswordExpiry();
        let finalAlerts = [...backendAlerts];
        let finalUnread = backendUnreadCount;

        if (vAlert) {
            // Check if already in list to avoid duplicates
            if (!finalAlerts.some(a => a.id === vAlert.id)) {
                finalAlerts = [vAlert, ...finalAlerts];
                // Badge Logic: backendUnreadCount + (isVirtualUnread ? 1 : 0)
                if (!vAlert.is_read) {
                    finalUnread += 1;
                }
            }
        }
        setAlerts(finalAlerts);
        setUnreadCount(finalUnread);
    }, [checkPasswordExpiry]);

    const fetchUnreadCount = useCallback(async () => {
        if (!user) return;
        const tok = token || getStoredToken();
        if (!tok) return;
        try {
            const response = await apiFetch(`/alerts/unread-count?include_tenant_alerts=${showTenantAlerts}`, null, { method: 'GET' });
            const vAlert = checkPasswordExpiry();
            const virtualUnread = (vAlert && !vAlert.is_read) ? 1 : 0;
            setUnreadCount((response.unread_count || 0) + virtualUnread);
        } catch (err) {
            console.error('Failed to fetch unread count:', err);
        }
    }, [user, token, showTenantAlerts, checkPasswordExpiry]);

    const fetchAlerts = useCallback(async (silent = false) => {
        if (!user) return;
        if (!silent) setLoading(true);
        try {
            const response = await apiFetch(`/alerts?limit=20&include_tenant_alerts=${showTenantAlerts}`, null, { method: 'GET' });
            injectVirtualAlert(response.alerts || [], response.unread_count || 0);
        } catch (err) {
            console.error('Failed to fetch alerts:', err);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [user, showTenantAlerts, injectVirtualAlert]);

    // Initial Fetch & Polling & Focus Sync
    useEffect(() => {
        if (user) {
            fetchAlerts(true);
            fetchUnreadCount();

            // Midnight Transition / Day-Marker Sync on focus
            const onFocus = () => {
                fetchAlerts(true);
                fetchUnreadCount();
            };
            window.addEventListener('focus', onFocus);

            const interval = setInterval(() => {
                fetchUnreadCount();
            }, 5000);

            return () => {
                clearInterval(interval);
                window.removeEventListener('focus', onFocus);
            };
        } else {
            setAlerts([]);
            setUnreadCount(0);
        }
    }, [user, fetchAlerts, fetchUnreadCount]);

    // Actions
    const markAsRead = async (alertId) => {
        if (typeof alertId === 'string' && (alertId.startsWith('virtual_') || alertId.startsWith('pwd-exp-'))) {
            const todayDateStr = dayjs().format('YYYY-MM-DD');
            const userId = user.id || user.email;
            const readKey = `password_alert_read_${userId}_${todayDateStr}`;
            localStorage.setItem(readKey, 'true');
            // Optimistic update
            setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_read: true } : a));
            setUnreadCount(prev => Math.max(0, prev - 1));
            return;
        }

        try {
            await apiFetch(`/alerts/${alertId}/read`, null, { method: 'PUT' });
            setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_read: true } : a));
            setUnreadCount(prev => Math.max(0, prev - 1));
            fetchUnreadCount();
        } catch (err) {
            console.error("Failed to mark as read", err);
            fetchAlerts(true);
        }
    };

    const markAllRead = async () => {
        // Handle virtual alert first
        const vAlert = checkPasswordExpiry();
        if (vAlert && !vAlert.is_read) {
            const userId = user.id || user.email;
            const todayDateStr = dayjs().format('YYYY-MM-DD');
            const readKey = `password_alert_read_${userId}_${todayDateStr}`;
            localStorage.setItem(readKey, 'true');
        }

        try {
            await apiFetch(`/alerts/mark-all-read?include_tenant_alerts=${showTenantAlerts}`, null, { method: 'PUT' });
            setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error("Failed to mark all read", err);
            fetchAlerts(true);
        }
    };

    const deleteAlert = async (alertId) => {
        if (typeof alertId === 'string' && (alertId.startsWith('virtual_') || alertId.startsWith('pwd-exp-'))) {
            const userId = user.id || user.email;
            const todayDateStr = dayjs().format('YYYY-MM-DD');
            const readKey = `password_alert_read_${userId}_${todayDateStr}`;
            localStorage.setItem(readKey, 'true');
            const vAlert = checkPasswordExpiry();
            setAlerts(prev => prev.filter(a => a.id !== alertId));
            setUnreadCount(prev => Math.max(0, (vAlert && !vAlert.is_read) ? prev - 1 : prev));
            return;
        }

        try {
            await apiFetch(`/alerts/${alertId}`, null, { method: 'DELETE' });
            setAlerts(prev => prev.filter(a => a.id !== alertId));
            fetchUnreadCount();
        } catch (err) {
            console.error("Failed to delete alert", err);
            fetchAlerts(true);
        }
    };

    const clearAllAlerts = async () => {
        const vAlert = checkPasswordExpiry();
        if (vAlert) {
            const userId = user.id || user.email;
            const todayDateStr = dayjs().format('YYYY-MM-DD');
            const readKey = `password_alert_read_${userId}_${todayDateStr}`;
            localStorage.setItem(readKey, 'true');
        }

        try {
            await apiFetch(`/alerts/clear?include_tenant_alerts=${showTenantAlerts}`, null, { method: 'DELETE' });
            setAlerts([]);
            setUnreadCount(0);
        } catch (err) {
            console.error("Failed to clear all alerts", err);
            fetchAlerts(true);
        }
    };

    const toggleTenantAlerts = () => {
        setShowTenantAlerts(prev => !prev);
    };

    const value = {
        alerts,
        unreadCount,
        loading,
        fetchAlerts,
        refreshAlerts: () => { fetchAlerts(true); fetchUnreadCount(); },
        markAsRead,
        markAllRead,
        clearAllAlerts,
        deleteAlert,
        showTenantAlerts,
        toggleTenantAlerts
    };

    return (
        <AlertContext.Provider value={value}>
            {children}
        </AlertContext.Provider>
    );
};
// export default AlertProvider; // Avoid default export if we use named exports extensively
// But wait, HMR issues often come from exporting the Context object itself alongside components.
// Let's try to export default the Provider, and keep named Context.
export default AlertProvider;
